import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  InputAdornment,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  CircularProgress, // Import CircularProgress for the loader
} from "@mui/material";
import {
  Visibility,
  Delete,
  Edit,
  Search,
  Close as CloseIcon,
} from "@mui/icons-material";
import { userManagement } from "../../config/images";
// Import the updated API calls and a new one for getting a single user
import {
  getUsers,
  getUserId,
  addUsers,
  updateUsers,
  deleteUsers,
} from "./services";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Add this line
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false); // State for Add User button loader
  const [isEditLoading, setIsEditLoading] = useState(false); // State for Edit User button loader
  const [isDeleteLoading, setIsDeleteLoading] = useState(false); // State for Delete User button loader
  const [isViewLoading, setIsViewLoading] = useState(false); // State for View user loading
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailError, setEmailError] = React.useState("");
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    status: "",
    plan: "",
  });
  const [editUser, setEditUser] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        // Pass the searchQuery to the getUsers API call
        const response = await getUsers(
          page,
          pageSize,
          statusFilter,
          searchQuery
        );
        if (response.data.success) {
          setUsers(response.data.payload.results);
          setTotalCount(response.data.payload.count);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [page, pageSize, statusFilter, searchQuery]); // Add searchQuery to the dependency array

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset page to 1 when search query changes
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleOpenAddUserDialog = () => {
    setOpenAddUserDialog(true);
  };

  const handleCloseAddUserDialog = () => {
    setOpenAddUserDialog(false);
    setNewUser({
      name: "",
      email: "",
      phone: "",
      role: "",
      status: "",
      plan: "",
    });
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser(user);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    setEditUser({});
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleOpenViewDialog = async (user) => {
    setIsViewLoading(true); // Start loading
    try {
      // Fetch the user data from the API
      const response = await getUserId(user.id);
      if (response.data.success) {
        setSelectedUser(response.data.payload);
        setOpenViewDialog(true);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toast.error("Failed to load user details.",{toastId:"view-error"});
    } finally {
      setIsViewLoading(false); // End loading
    }
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditUser({ ...editUser, [name]: value });
  };

  const handleAddUser = async () => {
    if (isAddLoading) return; // stop if already clicked

    setIsAddLoading(true); // lock immediately

    // Common toast options with unique IDs
    const toastOptions = { toastId: "addUserToast" };

    if (!newUser.name) {
      toast.error("Name is required", toastOptions,{toastId:"name-error"});
      setIsAddLoading(false);
      return;
    }
    if (!newUser.email) {
      toast.error("Email is required", toastOptions,{toastId:"email-error"});
      setIsAddLoading(false);
      return;
    }
    if (!newUser.phone) {
      toast.error("Phone is required", toastOptions,{toastId:"phone-error"});
      setIsAddLoading(false);
      return;
    }
    if (!newUser.role) {
      toast.error("Role is required", toastOptions,{toastId:"role-error"});
      setIsAddLoading(false);
      return;
    }
    if (!newUser.status) {
      toast.error("Status is required", toastOptions,{toastId:"status-error"});
      setIsAddLoading(false);
      return;
    }
    if (!newUser.plan) {
      toast.error("Plan is required", toastOptions,{toastId:"plan-error"});
      setIsAddLoading(false);
      return;
    }

    try {
      const payload = {
        email: newUser.email,
        name: newUser.name,
        phone: newUser.phone,
        role: newUser.role,
        is_active: newUser.status === "Active",
        is_suspended: newUser.status === "Suspended",
        plan: newUser.plan,
      };

      const response = await addUsers(payload);

      if (response.data.success) {
        toast.success(response.data.message, { toastId: "addUserSuccess" });
        const updatedUsersResponse = await getUsers(
          page,
          pageSize,
          statusFilter,
          searchQuery
        );
        if (updatedUsersResponse.data.success) {
          setUsers(updatedUsersResponse.data.payload.results);
          setTotalCount(updatedUsersResponse.data.payload.count);
        }
        setNewUser({
          name: "",
          email: "",
          phone: "",
          role: "",
          status: "",
          plan: "",
        });
        handleCloseAddUserDialog();
      }
    } catch (error) {
      console.error("Add user error:", error); // Add this for debugging
      
      // Improved error message handling for 400 and other errors
      let errorMessage = "Failed to add user";
      
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        if (data) {
          // Try different possible error message fields
          errorMessage = data.message || 
                        data.error || 
                        data.detail || 
                        data.non_field_errors?.[0] ||
                        (typeof data === 'string' ? data : errorMessage);
          
          // Handle validation errors (common in 400 responses)
          if (data.errors && typeof data.errors === 'object') {
            const firstError = Object.values(data.errors)[0];
            if (Array.isArray(firstError)) {
              errorMessage = firstError[0];
            } else {
              errorMessage = firstError;
            }
          }
        }
        
        // Add status code context for debugging
        if (status === 400) {
          console.log("400 Error details:", data);
          toast.error(data.message,{toastId:"add-error"});
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error. Please check your connection.";
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
        toast.error(errorMessage, {
          toastId: "addUserError",
        });
      }
      
    } finally {
      setIsAddLoading(false); // release button
    }
  };

  const handleEditUser = async () => {
    if (isEditLoading) return; // Prevent multiple clicks

    setIsEditLoading(true); // lock immediately

    // Common toast options with unique IDs

    // Validation checks
    if (!editUser.name || editUser.name.trim() === "") {
      toast.error("Name is required", {toastId:"edit-name-error"});
      setIsEditLoading(false);
      return;
    }
    if (!editUser.email || editUser.email.trim() === "") {
      toast.error("Email is required", {toastId:"edit-email-error"});
      setIsEditLoading(false);
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editUser.email)) {
      toast.error("Please enter a valid email address", {toastId:"edit-email-error"});
      setIsEditLoading(false);
      return;
    }
    
    if (!editUser.phone || editUser.phone.trim() === "") {
      toast.error("Phone is required", {toastId:"edit-phone-error"});
      setIsEditLoading(false);
      return;
    }
    
    // Phone number validation (10 digits)
    // if (editUser.phone.length !== 10 || !/^\d{10}$/.test(editUser.phone)) {
    //   toast.error("Phone number must be exactly 10 digits", {toastId:"edit-phone-error"});
    //   setIsEditLoading(false);
    //   return;
    // }
    
    if (!editUser.role) {
      toast.error("Role is required", {toastId:"edit-role-error"});
      setIsEditLoading(false);
      return;
    }
    if (!editUser.status) {
      toast.error("Status is required", {toastId:"edit-status-error"});
      setIsEditLoading(false);
      return;
    }
    if (!editUser.plan) {
      toast.error("Plan is required", {toastId:"edit-plan-error"});
      setIsEditLoading(false);
      return;
    }

    try {
      const payload = {
        email: editUser.email.trim(),
        name: editUser.name.trim(),
        phone: editUser.phone,
        role: editUser.role,
        is_active: editUser.status === "Active",
        is_suspended: editUser.status === "Suspended",
        plan: editUser.plan || null,
      };

      const response = await updateUsers(editUser.id, payload);

      if (response.data.success) {
        toast.success(response.data.message, { toastId: "editUserSuccess" });
        const updatedUsersResponse = await getUsers(
          page,
          pageSize,
          statusFilter,
          searchQuery
        );
        if (updatedUsersResponse.data.success) {
          setUsers(updatedUsersResponse.data.payload.results);
          setTotalCount(updatedUsersResponse.data.payload.count);
        }
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error("Edit user error:", error);
      
      // Improved error message handling for 400 and other errors
      let errorMessage = "Failed to edit user";
      
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        if (data) {
          // Try different possible error message fields
          errorMessage = data.message || 
                        data.error || 
                        data.detail || 
                        data.non_field_errors?.[0] ||
                        (typeof data === 'string' ? data : errorMessage);
          
          // Handle validation errors (common in 400 responses)
          if (data.errors && typeof data.errors === 'object') {
            const firstError = Object.values(data.errors)[0];
            if (Array.isArray(firstError)) {
              errorMessage = firstError[0];
            } else {
              errorMessage = firstError;
            }
          }
          
          // Add status code context for debugging
          if (status === 400) {
            console.log("400 Error details:", data);
          }
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error. Please check your connection.";
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage, {
        toastId: "editUserError",
      });
    } finally {
      setIsEditLoading(false); // unlock button
    }
  };

  const handleDeleteUser = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await deleteUsers(selectedUser.id);
      if (response.data.success) {
        toast.success(response.data.success,{toastId:"delete-success"});
        // Fetch users again to update the list
        const updatedUsersResponse = await getUsers(
          page,
          pageSize,
          statusFilter,
          searchQuery
        );
        if (updatedUsersResponse.data.success) {
          setUsers(updatedUsersResponse.data.payload.results);
          setTotalCount(updatedUsersResponse.data.payload.count);
        }
        handleCloseDeleteDialog();
      }
    } catch (error) {
      toast.error(error.response.data.message,{toastId:"delete-error"});
      console.error("Failed to delete user:", error);
    } finally {
      setOpenDeleteDialog(false);
      setIsDeleteLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
      
      <Box
        sx={{
          background: "#ffffff",
          padding: "30px",
          border: "1px solid #1570EF",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize:{xs: "24px", md:"36px", sm:"32px"},
              fontWeight: 600,
            }}
          >
            User Management
          </Typography>
          <button
            variant="contained"
            className="export-report-btn"
            style={{ fontWeight: "600", padding: "10px 20px" }}
            onClick={handleOpenAddUserDialog}
          >
            Add New User
          </button>
        </Box>

        {/* Search + Filters */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search Users"
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { md: 300 },
              bgcolor: "white",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          />

          {/* Buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={handleStatusChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Suspended">Suspended</MenuItem>
            </TextField>
            <button
              variant="contained"
              className="export-report-btn"
              style={{ fontWeight: "600", padding: "7px 20px" }}
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("All");
              }}
            >
              Reset
            </button>
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ width: "100%", overflow: "auto" }}>
          <Table className="api-integration-table">
            <TableHead sx={{ bgcolor: "#f5f7fa" }}>
              <TableRow>
                <TableCell>U.ID</TableCell>
                <TableCell>USER</TableCell>
                <TableCell>EMAIL</TableCell>
                <TableCell>ROLE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>PLAN</TableCell>
                <TableCell>LAST LOGIN</TableCell>
                <TableCell>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.id}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.status}
                        size="small"
                        sx={{
                          bgcolor:
                            u?.status === "Active"
                              ? "#E3FFEC"
                              : u?.status === "Inactive"
                              ? "#DBEAFE"
                              : "rgba(0,0,0,0.08)",
                          color:
                            u?.status === "Active"
                              ? "#5F9A72"
                              : u?.status === "Inactive"
                              ? "#1E40AF"
                              : "gray",
                          fontWeight: "bold",
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>
                    <TableCell>{u.plan}</TableCell>
                    <TableCell>{u.last_login}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: "15px" }}>
                        <img
                          src={userManagement.viewUser}
                          alt=""
                          style={{ objectFit: "contain", cursor: "pointer" }}
                          onClick={() => handleOpenViewDialog(u)}
                        />
                        <img
                          src={userManagement.editUser}
                          alt=""
                          style={{ objectFit: "contain", cursor: "pointer" }}
                          onClick={() => handleOpenEditDialog(u)}
                        />
                        <img
                          src={userManagement.deleteUser}
                          alt=""
                          style={{ objectFit: "contain", cursor: "pointer" }}
                          onClick={() => handleOpenDeleteDialog(u)}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2">
            Showing {(page - 1) * pageSize + 1} To{" "}
            {Math.min(page * pageSize, totalCount)} Of {totalCount} Results
          </Typography>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
          />
        </Box>
      </Box>

      {/* Add New User Dialog */}
      <Dialog
        open={openAddUserDialog}
        onClose={handleCloseAddUserDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
            maxWidth: "500px",
          },
        }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "600", fontSize: "24px" }}
        >
          Add New User
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            User Name
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            placeholder="User name"
            fullWidth
            variant="outlined"
            size="small"
            name="name"
            value={newUser.name}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only alphabets and space
              if (/^[A-Za-z\s]*$/.test(value)) {
                handleInputChange(e);
              }
            }}
            sx={{ mt: 0 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Email
          </Typography>
          <TextField
            margin="dense"
            placeholder="Email"
            type="email"
            fullWidth
            variant="outlined"
            size="small"
            name="email"
            value={newUser.email}
            onChange={(e) => {
              handleInputChange(e);

              const value = e.target.value;
              // Simple email regex
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (value && !emailRegex.test(value)) {
                setEmailError("Please enter a valid email");
              } else {
                setEmailError("");
              }
            }}
            error={Boolean(emailError)}
            helperText={emailError}
            sx={{ mt: 0 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Phone
          </Typography>
          <PhoneInput
            defaultCountry="us"
            value={newUser.phone}
            onChange={(phone) =>
              handleInputChange({
                target: {
                  name: "phone",
                  value: phone,
                },
              })
            }
            placeholder="Contact Number"
            inputProps={{
              name: "phone",
              required: true,
              autoFocus: true,
              style: {
                height: "40px", // same as other inputs
                width: "100%",
              },
            }}
            style={{
              "--react-international-phone-height": "40px", // change from 56px to 40px
              "--react-international-phone-border-color": "#c4c4c4",
              "--react-international-phone-border-radius": "4px",
              width: "100%",
            }}
            sx={{
              mb: 2.5,
              "& .react-international-phone-input": {
                border: "1px solid #c4c4c4",
                height: "40px", // enforce same height for inner input
                "&:hover": {
                  borderColor: "#1570ef",
                },
                "&:focus": {
                  borderColor: "#1570ef",
                },
              },
            }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Role
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="role"
            value={newUser.role}
            onChange={handleInputChange}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select Role
            </MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </Select>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Status
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="status"
            value={newUser.status}
            onChange={handleInputChange}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select Status
            </MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="Suspended">Suspended</MenuItem>
          </Select>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Plan
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="plan"
            value={newUser.plan}
            onChange={handleInputChange}
          >
            <MenuItem value="" disabled>
              Select Plan
            </MenuItem>
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="premium">Premium</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleCloseAddUserDialog}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={isAddLoading} // Disable the button while loading
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {isAddLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add User"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
            maxWidth: "500px",
          },
        }}
        onClose={handleCloseEditDialog}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "600", fontSize: "24px" }}
        >
          Edit User
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            User Name
          </Typography>
          <TextField
            margin="dense"
            placeholder="User Name"
            fullWidth
            variant="outlined"
            size="small"
            name="name"
            value={editUser.name || ""}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only alphabets and spaces (no numbers)
              if (/^[A-Za-z\s]*$/.test(value)) {
                handleEditInputChange(e);
              }
            }}
            sx={{ mt: 0 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Email
          </Typography>
          <TextField
            margin="dense"
            placeholder="Email"
            type="email"
            fullWidth
            variant="outlined"
            size="small"
            name="email"
            value={editUser.email || ""}
            onChange={handleEditInputChange}
            sx={{ mt: 0 }}
          />
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Phone
          </Typography>
          <PhoneInput
  defaultCountry="us"
  value={editUser.phone || ""}
  onChange={(phone) =>
    handleEditInputChange({
      target: {
        name: "phone",
        value: phone,
      },
    })
  }
  placeholder="Contact Number"
  inputProps={{
    name: "phone",
    required: true,
    autoFocus: true,
    style: {
      height: "40px", // same height as other inputs
      width: "100%",
    },
  }}
  style={{
    "--react-international-phone-height": "40px", // consistent height
    "--react-international-phone-border-color": "#c4c4c4",
    "--react-international-phone-border-radius": "4px",
    width: "100%",
  }}
  sx={{
    mb: 2.5,
    "& .react-international-phone-input": {
      border: "1px solid #c4c4c4",
      height: "40px", // enforce same height
      "&:hover": {
        borderColor: "#1570ef",
      },
      "&:focus": {
        borderColor: "#1570ef",
      },
    },
  }}
/>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Role
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="role"
            value={editUser.role || ""}
            onChange={handleEditInputChange}
            sx={{ mt: 0 }}
          >
            <MenuItem value="" disabled>
              Select Role
            </MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </Select>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Status
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="status"
            value={editUser.status || ""}
            onChange={handleEditInputChange}
          >
            <MenuItem value="" disabled>
              Select Status
            </MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="Suspended">Suspended</MenuItem>
          </Select>
          <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
            Plan
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            name="plan"
            value={editUser.plan || ""}
            onChange={handleEditInputChange}
          >
            <MenuItem value={"-" || ""} disabled>
              Select Plan
            </MenuItem>
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="premium">Premium</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleCloseEditDialog}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditUser}
            variant="contained"
            disabled={isEditLoading} // Disable the button while loading
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {isEditLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Update User"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "600",
            mt: 1.5,
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", my: 2, mb: 0 }}>
          <Typography
            sx={{ fontWeight: 400, fontSize: "16px", color: "#333333" }}
          >
            Are you sure you want to delete this user?
          </Typography>
          <Typography
            variant="body2"
            color="#333333"
            sx={{ fontSize: "16px", mt: 1 }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: "16px 24px" }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              border: "1px solid #1570EF",
              color: "#1570EF",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            disabled={isDeleteLoading} // Disable the button while loading
            sx={{
              textTransform: "capitalize",
              padding: "10px 20px",
              background: "#1570EF",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": {
                background: "#1570EF",
              },
            }}
          >
            {isDeleteLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={openViewDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "450px",
            maxWidth: "450px",
            borderRadius: "12px",
          },
        }}
        onClose={handleCloseViewDialog}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "18px",
            pb: 1,
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          View Details
          <IconButton
            onClick={handleCloseViewDialog}
            size="small"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          {isViewLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            selectedUser &&
            [
              { label: "User ID:", value: selectedUser?.id },
              { label: "User Name:", value: selectedUser?.name },
              { label: "Email:", value: selectedUser?.email },
              { label: "Phone:", value: selectedUser?.phone || "-" },
              { label: "Role:", value: selectedUser?.role },
              {
                label: "Status:",
                value: (
                  <Chip
                    label={selectedUser?.status}
                    size="small"
                    sx={{
                      bgcolor:
                        selectedUser?.status === "Active"
                          ? "#E3FFEC"
                          : selectedUser?.status === "Inactive"
                          ? "#DBEAFE"
                          : "rgba(0,0,0,0.08)",
                      color:
                        selectedUser?.status === "Active"
                          ? "#5F9A72"
                          : selectedUser?.status === "Inactive"
                          ? "#1E40AF"
                          : "gray",

                      borderRadius: "6px",
                      height: "22px",
                      fontWeight: 400,
                    }}
                  />
                ),
              },
              { label: "Plan:", value: selectedUser?.plan || "-" },
              { label: "Join Date:", value: selectedUser?.join_date || "-" },
              { label: "Last Login:", value: selectedUser?.last_login || "-" },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "120px", fontWeight: 400 }}
                >
                  {item.label}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {item.value}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
